import React, {useContext, useEffect, useMemo, useRef} from "react";
import classnames from "classnames";
import UserWebHomepageProductCard from "../../../product-card";
import {Fade, Tooltip} from "@mui/material";
import UserWebHomepageProductSlider from "../../../product-slider";
import {
    UserHomepageComponentTypes,
    UserHomepageControlPanelEvents,
    UserHomepageEventEmitter,
    UserHomepageProductTypes,
    UserHomepageViewMode
} from "../../../../../../models";
import {UserHomepageControllerContext, UserHomepageStateContext, UserHomepageViewContext} from "../../../../../../index";

const sliderItemsPerPage = {
    auto: 5,
    xs: 1,
    sm: 2,
    md: 4,
    lg: 5,
    xl: 6,
    xxl: 7,
};

const UserWebHomepageProducts = ({component, tabId, hideHeader = false}) => {
    const {viewMode} = useContext(UserHomepageStateContext);
    const controller = useContext(UserHomepageControllerContext);
    const type = useMemo(() => {
        switch (component?.type?.id) {
            case UserHomepageComponentTypes?.smallProduct:
                return UserHomepageProductTypes.small;
            case UserHomepageComponentTypes?.mediumProduct:
            default:
                return UserHomepageProductTypes.medium;
            case UserHomepageComponentTypes?.largeProduct:
                return UserHomepageProductTypes.large;
        }
    }, [component])
    const {viewportWidth} = useContext(UserHomepageViewContext);
    const products = useMemo(() => component?.content?.parts ?? [], [component]);
    /**@type {React.MutableRefObject<import('react-owl-carousel/umd/OwlCarousel.d.ts').default>}*/
    const carouselRef = useRef();

    /** @type{boolean} */
    const showMoreButton = useMemo(() =>
            (!!component.searchQuery && viewMode === UserHomepageViewMode.preview) ||
            (viewMode === UserHomepageViewMode.edit),
        [viewMode, component?.searchQuery])
    /** @type{boolean}*/
    const moreButtonInvisible = useMemo(() =>
            viewMode === UserHomepageViewMode.edit && !component.nexusSearchId,
        [viewMode, component?.nexusSearchId])

    const productGridModifier = useMemo(() => {
        switch (type) {
            case UserHomepageProductTypes.large:
                return 'user-web-homepage-products-grid--large';
            case UserHomepageProductTypes.medium:
            default:
                return 'user-web-homepage-products-grid--medium';
        }
    }, [type])

    /**
     * With each change in the controller:
     * - attaches the event listeners for content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        return () => {
            controller.off(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        }
    }, [component, controller])


    /**
     * Uses the owl carousel api to bring the selected content into view.
     *
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    const focusComponentContent = (tabId, componentId, contentId) => {
        if (componentId !== component.id)
            return;
        switch (component.type?.id) {
            case UserHomepageComponentTypes.smallProduct:
                const contentIndex = component.content?.parts?.findIndex(e => e.id === contentId);
                if (contentIndex === -1)
                    return;
                try {
                    const itemsPerPage = sliderItemsPerPage[viewportWidth] ?? sliderItemsPerPage.auto;
                    const pageIndex = Math.floor(contentIndex / itemsPerPage);
                    carouselRef.current.$ele?.trigger('to.owl.carousel', [pageIndex, 250]);
                } catch (e) {
                }
                break;
            default:
                break;
        }
    }

    /**
     * Invokes the appropriate controller callback for the click of the products more button.
     */
    const onMoreButtonClicked = () => {
        controller.selectComponentSearchButton(tabId, component.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the selection of a product in a slider.
     * @param {any} product
     */
    const onProductSelected = (product) => {
        if (!product)
            return;
        controller.selectContent(tabId, component.id, product.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the click of a product.
     * @param {any} product
     */
    const onProductClicked = (product) => {
        controller.clickContent(tabId, component.id, product.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the click of the tag of a tag.
     *
     * @param {any} product
     * @param {any} tag
     */
    const onTagClicked = (product, tag) => {
        controller.selectProductTag(tabId, component.id, product.id, tag.id, UserHomepageEventEmitter.view);
    }

    /**@type {JSX.Element}*/
    const moreButton = useMemo(() => (
        <button
            onClick={onMoreButtonClicked}
            className={classnames('user-web-homepage-more-button', {'not-visible': moreButtonInvisible})}>
            See All
        </button>
    ), [moreButtonInvisible])

    /**
     * Renders the container of the product based on the type of the product.
     *
     * * if small: renders the slider.
     * @return {JSX.Element}
     */
    const container = useMemo(() => {
        switch (type) {
            case UserHomepageProductTypes.small:
                return <UserWebHomepageProductSlider
                    type={type}
                    products={products}
                    onSelectionChanged={onProductSelected}
                    onProductClicked={onProductClicked}
                    onTagClicked={onTagClicked}
                    ref={carouselRef}
                />
            default:
                return (
                    <div className={classnames('user-web-homepage-products-grid', productGridModifier)}>
                        {
                            products.map((product) => (
                                <UserWebHomepageProductCard
                                    key={product.id}
                                    data={product}
                                    type={type}
                                    fullWidth
                                    onProductClicked={onProductClicked}
                                    onTagClicked={onTagClicked}
                                />
                            ))
                        }
                    </div>
                );

        }
    }, [products, type, productGridModifier, onProductSelected, onProductClicked, onTagClicked])


    const isTimeBased = component?.type?.id === UserHomepageComponentTypes.timeBased || hideHeader;

    return (
        <>
            <div className={'user-web-homepage-products'}>
                {
                    !hideHeader &&
                    <div className={'user-web-homepage-block-header component-title'}>
                        <p>
                            {component?.title ?? ''}
                        </p>
                        <Fade in={showMoreButton}>
                            {
                                moreButtonInvisible
                                    ? <Tooltip
                                        title={'No search query or search type specified'}
                                    >
                                        {moreButton}
                                    </Tooltip>
                                    : moreButton
                            }
                        </Fade>
                    </div>
                }
                {
                    isTimeBased
                        ? <div className={'user-web-homepage-time-based-products'}>{container}</div>
                        : container
                }
            </div>
        </>
    );
}

export default UserWebHomepageProducts;
